import { useEffect, useState } from "preact/hooks"
import { capture_event } from "@/utils/analytics"
import { Loader2, Wand2 } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { exactCurrencyFormatter } from "@/utils"
import { toast } from "@/components/ui/use-toast.ts"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"
import {
  applyAutoCode,
  previewAutoCode,
  type AutoCodePreviewResponse,
  type AutoCodePreviewSplit,
  type AutoCodeSource,
} from "@/api/accounting.tsx"

export type AutoCodeScopeChoice = "check" | "all"

const SOURCE_LABEL: Record<AutoCodeSource, string> = {
  category_rule: "Rule",
  early_pay: "Early-pay",
  repayment_mirror: "Mirrors repaid deduction",
}

function split_account_label(split: AutoCodePreviewSplit): string {
  const label = [split.account_number, split.account_name].filter(Boolean).join(" · ")
  return label || "Unknown account"
}

interface AutoCodePreviewDialogProps {
  open: boolean
  onClose: () => void
  check_number: string
  /** Every transaction on the check; auto-code only touches the uncoded ones. */
  check_deduction_ids: string[]
}

/** Previews the deductions the org's simple coding rules would code, on this check (default) or
 *  across every uncoded deduction, and applies exactly the previewed rows. */
export function AutoCodePreviewDialog({ open, onClose, check_number, check_deduction_ids }: AutoCodePreviewDialogProps) {
  const [scope, setScope] = useState<AutoCodeScopeChoice>("check")
  const [preview, setPreview] = useState<AutoCodePreviewResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset to the default scope each time the dialog opens.
  useEffect(() => {
    if (open) setScope("check")
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setPreview(null)
    previewAutoCode(scope === "check" ? check_deduction_ids : undefined)
      .then(res => !cancelled && setPreview(res))
      .catch(() => !cancelled && setError("Couldn't load the auto-code preview."))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [open, scope, check_deduction_ids.join(",")])

  async function handle_apply() {
    if (!preview || preview.rows.length === 0) return
    setApplying(true)
    try {
      const res = await applyAutoCode(preview.rows.map(r => r.deduction_id))
      capture_event("auto_code_applied", { scope, coded_count: res.coded_count })
      toast({
        title: "Auto-code complete",
        description: `Coded ${res.coded_count} deduction${res.coded_count === 1 ? "" : "s"}.`,
      })
      triggerRefreshFlag()
      onClose()
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to auto-code deductions" })
    } finally {
      setApplying(false)
    }
  }

  const rows = preview?.rows ?? []
  const total = rows.reduce((sum, r) => sum + r.splits.reduce((s, split) => s + Number(split.amount), 0), 0)
  const not_coded = preview ? preview.uncoded_count - rows.length : 0
  const show_check_column = scope === "all"

  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Preview auto code</DialogTitle>
          <DialogDescription>
            Deductions your simple coding rules would code. Nothing is written until you apply.
          </DialogDescription>
        </DialogHeader>

        <div class="flex items-center gap-4 text-sm" role="radiogroup" aria-label="Auto-code scope">
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="auto-code-scope" checked={scope === "check"} onChange={() => setScope("check")} />
            Only check #{check_number}
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="auto-code-scope" checked={scope === "all"} onChange={() => setScope("all")} />
            All uncoded deductions
          </label>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto border rounded-md">
          {loading && (
            <div class="flex items-center gap-2 p-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking rules…
            </div>
          )}
          {error && <div class="p-4 text-sm text-red-700">{error}</div>}
          {preview && rows.length === 0 && (
            <div class="p-6 text-sm text-slate-500">
              {preview.uncoded_count === 0
                ? "There are no uncoded deductions in this scope."
                : "None of the uncoded deductions in this scope match a simple coding rule."}
            </div>
          )}
          {rows.length > 0 && (
            <table class="w-full text-sm">
              <thead class="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  {show_check_column && <th class="px-3 py-2">Check</th>}
                  <th class="px-3 py-2">Invoice</th>
                  <th class="px-3 py-2">Category</th>
                  <th class="px-3 py-2">GL account</th>
                  <th class="px-3 py-2">Source</th>
                  <th class="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row =>
                  row.splits.map((split, i) => (
                    <tr key={`${row.deduction_id}-${i}`} class={i === 0 ? "border-t" : ""}>
                      {show_check_column && <td class="px-3 py-1.5 text-slate-600">{i === 0 ? row.check_number ?? "—" : ""}</td>}
                      <td class="px-3 py-1.5">{i === 0 ? row.invoice_number : ""}</td>
                      <td class="px-3 py-1.5 text-slate-600">{i === 0 ? row.category_name ?? "—" : ""}</td>
                      <td class="px-3 py-1.5">{split_account_label(split)}</td>
                      <td class="px-3 py-1.5 text-slate-500">{i === 0 ? SOURCE_LABEL[row.source] : ""}</td>
                      <td class="px-3 py-1.5 text-right tabular-nums">{exactCurrencyFormatter(Number(split.amount))}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {preview && (
          <div class="text-sm text-slate-600">
            {rows.length} of {preview.uncoded_count} uncoded deduction{preview.uncoded_count === 1 ? "" : "s"} will be coded
            {rows.length > 0 && <> for {exactCurrencyFormatter(total)}</>}.
            {not_coded > 0 && <> {not_coded} have no matching rule{preview.skipped_zero_amount > 0 ? " or a $0 amount" : ""} and stay uncoded.</>}
          </div>
        )}

        <div class="flex justify-end gap-3 border-t border-slate-200 pt-4">
          <Button variant="outline" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button onClick={() => void handle_apply()} disabled={applying || loading || rows.length === 0}>
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {scope === "check" ? "Set codes on this check" : `Set codes on all ${rows.length}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
